// ─────────────────────────────────────────────────────────────────────────────
// Campo de fecha de la barra de Reportería, siempre en DÍA/MES/AÑO (sesión 2026-10-02, BJS-4).
//
// El <input type="date"> nativo pinta la fecha con el formato regional del NAVEGADOR: en un equipo
// con Windows en inglés se veía mes/día/año y en la sesión se eligió enero queriendo septiembre. Aquí
// la fecha se escribe y se lee como dd/mm/aaaa y el calendario nativo sigue disponible con el botón
// (`showPicker`). El valor que sale es el mismo ISO «aaaa-mm-dd» de siempre.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';

/** «aaaa-mm-dd» → «dd/mm/aaaa». Vacío o inválido ⇒ ''. */
export function isoADma(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** «dd/mm/aaaa» (acepta «d/m/aaaa» y guiones) → «aaaa-mm-dd». Fecha imposible ⇒ null. */
export function dmaAIso(texto: string): string | null {
  const m = /^\s*(\d{1,2})[/-](\d{1,2})[/-](\d{4})\s*$/.exec(texto ?? '');
  if (!m) return null;
  const [dia, mes, anio] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  if (fecha.getUTCFullYear() !== anio || fecha.getUTCMonth() !== mes - 1 || fecha.getUTCDate() !== dia) return null;
  return `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

export function CampoFecha({
  value, onChange, ariaLabel, abrirCalendario,
}: Readonly<{ value: string; onChange: (iso: string) => void; ariaLabel: string; abrirCalendario: string }>) {
  const [texto, setTexto] = useState(isoADma(value));
  const nativo = useRef<HTMLInputElement>(null);

  // Si la fecha cambia desde fuera (limpiar filtros, elegir campaña), el texto la sigue.
  useEffect(() => { setTexto(isoADma(value)); }, [value]);

  const confirmar = () => {
    if (texto.trim() === '') { onChange(''); return; }
    const iso = dmaAIso(texto);
    if (iso) onChange(iso);
    else setTexto(isoADma(value)); // inválida: se vuelve a la última buena en vez de filtrar con basura
  };

  return (
    <span className="rr-campo-fecha">
      <input
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => { if (e.key === 'Enter') confirmar(); }}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        className="rr-campo-fecha-btn"
        aria-label={abrirCalendario}
        onClick={() => {
          try { nativo.current?.showPicker(); } catch { nativo.current?.focus(); }
        }}
      >
        <i className="fas fa-calendar-days" aria-hidden="true" />
      </button>
      <input
        ref={nativo}
        type="date"
        className="rr-campo-fecha-nativo"
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  );
}
